import React, { useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

type Bootstrap = {
  products: any[];
  suppliers: any[];
  warehouses: any[];
  locations: any[];
  product_types: any[];
};

const emptyBootstrap: Bootstrap = {
  products: [], suppliers: [], warehouses: [], locations: [], product_types: [],
};

function errorMessage(error: unknown) {
  return error instanceof ApiClientError ? error.message : "No se pudo completar la operación.";
}

function idempotencyKey() {
  return `inventory-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function InventoryOperations() {
  const { hasPermission } = useAuth();
  const { show } = useToast();
  const [tab, setTab] = useState("stock");
  const [bootstrap, setBootstrap] = useState<Bootstrap>(emptyBootstrap);
  const [stock, setStock] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [receipt, setReceipt] = useState({
    product_id: "", supplier_id: "", warehouse_id: "", quantity: "",
    unit_cost: "", lot_number: "", serial_number: "", expiry_date: "", reason: "",
  });
  const [product, setProduct] = useState({
    sku: "", name: "", product_type: "INSUMO", presentation: "",
    purchase_unit: "unidad", consumption_unit: "unidad", conversion_factor: "1",
    preferred_supplier_id: "", lot_control: true, serial_control: false,
    expiry_control: true, min_stock: "0", ideal_stock: "0",
  });
  const [supplier, setSupplier] = useState({ legal_name: "", trade_name: "", tax_id: "", email: "", phone: "" });
  const [warehouse, setWarehouse] = useState({ location_id: "", code: "", name: "" });

  const selectedProduct = useMemo(
    () => bootstrap.products.find((item) => String(item.id) === receipt.product_id),
    [bootstrap.products, receipt.product_id]
  );

  async function load() {
    setLoading(true);
    try {
      const [base, balances, history] = await Promise.all([
        api.getInventoryBootstrap(), api.getInventoryStock(), api.getInventoryMovements(),
      ]);
      setBootstrap(base as Bootstrap);
      setStock(balances as any[]);
      setMovements(history as any[]);
    } catch (error) {
      show(errorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function submit(action: () => Promise<unknown>, success: string) {
    setSaving(true);
    try {
      await action();
      show(success, "success");
      await load();
    } catch (error) {
      show(errorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  }

  const tabs = [
    ["stock", "Existencias"], ["receive", "Nueva recepción"],
    ["products", "Productos"], ["suppliers", "Proveedores"],
    ["warehouses", "Depósitos"], ["movements", "Movimientos"],
  ];

  return (
    <div className="inventory-page">
      <div className="kanban-header">
        <div>
          <h1>Inventario</h1>
          <p className="meta">Stock por depósito y lote, con movimientos auditables.</p>
        </div>
        <button className="btn-ghost" onClick={() => void load()} disabled={loading}>Actualizar</button>
      </div>

      <div className="inventory-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {loading && <div className="loading-strip">Cargando inventario…</div>}

      {!loading && tab === "stock" && (
        <div className="chart-card">
          <h3>Existencias actuales</h3>
          {stock.length === 0 ? <p className="meta">Todavía no hay stock registrado.</p> : (
            <div className="table-wrap"><table><thead><tr>
              <th>Producto</th><th>Depósito</th><th>Lote / serie</th><th>Vencimiento</th><th>Cantidad</th>
            </tr></thead><tbody>{stock.map((row) => (
              <tr key={row.id}><td><b>{row.product_name}</b><div className="meta">{row.sku}</div></td>
                <td>{row.warehouse_name}</td><td>{row.lot_number || row.serial_number || "Sin lote"}</td>
                <td>{row.expiry_date || "—"}</td><td>{row.quantity} {row.consumption_unit}</td></tr>
            ))}</tbody></table></div>
          )}
        </div>
      )}

      {!loading && tab === "receive" && (
        <div className="chart-card inventory-form">
          <h3>Nueva recepción</h3>
          {!hasPermission("inventory.receive") ? <p className="meta">No tenés permiso para registrar recepciones.</p> : <>
            <div className="chart-form-row">
              <label><span>Producto</span><select value={receipt.product_id} onChange={(e:any) => setReceipt({...receipt, product_id:e.target.value})}>
                <option value="">Seleccionar…</option>{bootstrap.products.filter(p=>p.active).map(p=><option key={p.id} value={p.id}>{p.name} · {p.sku}</option>)}
              </select></label>
              <label><span>Depósito</span><select value={receipt.warehouse_id} onChange={(e:any) => setReceipt({...receipt, warehouse_id:e.target.value})}>
                <option value="">Seleccionar…</option>{bootstrap.warehouses.filter(w=>w.active).map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
              </select></label>
            </div>
            <div className="chart-form-row">
              <label><span>Proveedor</span><select value={receipt.supplier_id} onChange={(e:any) => setReceipt({...receipt, supplier_id:e.target.value})}>
                <option value="">Sin proveedor</option>{bootstrap.suppliers.filter(s=>s.active).map(s=><option key={s.id} value={s.id}>{s.trade_name || s.legal_name}</option>)}
              </select></label>
              <label><span>Cantidad recibida ({selectedProduct?.purchase_unit || "unidad de compra"})</span><input type="number" step="any" value={receipt.quantity} onChange={(e:any)=>setReceipt({...receipt,quantity:e.target.value})}/></label>
            </div>
            <div className="chart-form-row">
              <label><span>Costo por {selectedProduct?.purchase_unit || "unidad de compra"}</span><input type="number" step="any" value={receipt.unit_cost} onChange={(e:any)=>setReceipt({...receipt,unit_cost:e.target.value})}/></label>
              <label><span>Lote {selectedProduct?.lot_control ? "(obligatorio)" : ""}</span><input value={receipt.lot_number} onChange={(e:any)=>setReceipt({...receipt,lot_number:e.target.value})}/></label>
            </div>
            <div className="chart-form-row">
              <label><span>Serie {selectedProduct?.serial_control ? "(obligatoria)" : ""}</span><input value={receipt.serial_number} onChange={(e:any)=>setReceipt({...receipt,serial_number:e.target.value})}/></label>
              <label><span>Vencimiento {selectedProduct?.expiry_control ? "(obligatorio)" : ""}</span><input type="date" value={receipt.expiry_date} onChange={(e:any)=>setReceipt({...receipt,expiry_date:e.target.value})}/></label>
            </div>
            {selectedProduct && receipt.quantity && Number(receipt.quantity) > 0 && (
              <p className="meta">
                Se incorporarán <b>{Number(receipt.quantity) * Number(selectedProduct.conversion_factor)} {selectedProduct.consumption_unit}</b>
                {receipt.unit_cost && Number(receipt.unit_cost) >= 0
                  ? ` · costo por ${selectedProduct.consumption_unit}: ${(Number(receipt.unit_cost) / Number(selectedProduct.conversion_factor)).toLocaleString("es-AR", { maximumFractionDigits: 6 })}`
                  : ""}
              </p>
            )}
            <label><span>Referencia / comprobante</span><input value={receipt.reason} onChange={(e:any)=>setReceipt({...receipt,reason:e.target.value})} placeholder="Ej. Factura A 0001-00001234"/></label>
            <button className="btn-primary" disabled={saving} onClick={() => void submit(
              () => api.createInventoryReceipt({ idempotency_key:idempotencyKey(), ...receipt, product_id:Number(receipt.product_id), supplier_id:receipt.supplier_id?Number(receipt.supplier_id):undefined, warehouse_id:Number(receipt.warehouse_id), quantity:Number(receipt.quantity), unit_cost:Number(receipt.unit_cost) }),
              "Recepción registrada y stock actualizado."
            )}>{saving ? "Registrando…" : "Confirmar recepción"}</button>
          </>}
        </div>
      )}

      {!loading && tab === "products" && <div className="chart-card inventory-form"><h3>Productos</h3>
        {hasPermission("inventory.catalog.manage") && <>
          <div className="chart-form-row"><label><span>SKU</span><input value={product.sku} onChange={(e:any)=>setProduct({...product,sku:e.target.value})}/></label><label><span>Nombre</span><input value={product.name} onChange={(e:any)=>setProduct({...product,name:e.target.value})}/></label></div>
          <div className="chart-form-row"><label><span>Tipo</span><select value={product.product_type} onChange={(e:any)=>setProduct({...product,product_type:e.target.value})}>{bootstrap.product_types.map(t=><option key={t.code} value={t.code}>{t.name}</option>)}</select></label><label><span>Presentación</span><input value={product.presentation} onChange={(e:any)=>setProduct({...product,presentation:e.target.value})}/></label></div>
          <div className="chart-form-row"><label><span>Unidad de compra</span><input value={product.purchase_unit} onChange={(e:any)=>setProduct({...product,purchase_unit:e.target.value})}/></label><label><span>Unidad de consumo</span><input value={product.consumption_unit} onChange={(e:any)=>setProduct({...product,consumption_unit:e.target.value})}/></label></div>
          <label><span>Factor de conversión</span><input type="number" min="0" step="any" value={product.conversion_factor} onChange={(e:any)=>setProduct({...product,conversion_factor:e.target.value})}/><small className="meta">Cantidad de unidades de consumo contenidas en cada unidad de compra. Ej.: 1 vial = 100 U.</small></label>
          <div className="inventory-checks"><label><input type="checkbox" checked={product.lot_control} onChange={(e:any)=>setProduct({...product,lot_control:e.target.checked})}/> Control de lote</label><label><input type="checkbox" checked={product.serial_control} onChange={(e:any)=>setProduct({...product,serial_control:e.target.checked})}/> Control de serie</label><label><input type="checkbox" checked={product.expiry_control} onChange={(e:any)=>setProduct({...product,expiry_control:e.target.checked})}/> Control de vencimiento</label></div>
          <button className="btn-primary" disabled={saving} onClick={() => void submit(()=>api.createInventoryProduct({...product, conversion_factor:Number(product.conversion_factor), min_stock:Number(product.min_stock), ideal_stock:Number(product.ideal_stock)}),"Producto creado.")}>Crear producto</button>
        </>}
        <div className="inventory-simple-list">{bootstrap.products.map(p=><div key={p.id}><b>{p.name}</b><span>{p.sku} · 1 {p.purchase_unit} = {p.conversion_factor} {p.consumption_unit}</span></div>)}</div>
      </div>}

      {!loading && tab === "suppliers" && <div className="chart-card inventory-form"><h3>Proveedores</h3>
        {hasPermission("inventory.catalog.manage") && <><div className="chart-form-row"><label><span>Razón social</span><input value={supplier.legal_name} onChange={(e:any)=>setSupplier({...supplier,legal_name:e.target.value})}/></label><label><span>Nombre comercial</span><input value={supplier.trade_name} onChange={(e:any)=>setSupplier({...supplier,trade_name:e.target.value})}/></label></div><button className="btn-primary" disabled={saving} onClick={()=>void submit(()=>api.createInventorySupplier(supplier),"Proveedor creado.")}>Crear proveedor</button></>}
        <div className="inventory-simple-list">{bootstrap.suppliers.map(s=><div key={s.id}><b>{s.trade_name||s.legal_name}</b><span>{s.tax_id||"Sin identificación fiscal"}</span></div>)}</div>
      </div>}

      {!loading && tab === "warehouses" && <div className="chart-card inventory-form"><h3>Depósitos</h3>
        {hasPermission("inventory.catalog.manage") && <><div className="chart-form-row"><label><span>Sede</span><select value={warehouse.location_id} onChange={(e:any)=>setWarehouse({...warehouse,location_id:e.target.value})}><option value="">Seleccionar…</option>{bootstrap.locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}</select></label><label><span>Código</span><input value={warehouse.code} onChange={(e:any)=>setWarehouse({...warehouse,code:e.target.value})}/></label></div><label><span>Nombre</span><input value={warehouse.name} onChange={(e:any)=>setWarehouse({...warehouse,name:e.target.value})}/></label><button className="btn-primary" disabled={saving} onClick={()=>void submit(()=>api.createInventoryWarehouse({...warehouse,location_id:Number(warehouse.location_id)}),"Depósito creado.")}>Crear depósito</button></>}
        <div className="inventory-simple-list">{bootstrap.warehouses.map(w=><div key={w.id}><b>{w.name}</b><span>{w.location_name} · {w.code}</span></div>)}</div>
      </div>}

      {!loading && tab === "movements" && <div className="chart-card"><h3>Movimientos recientes</h3><div className="table-wrap"><table><thead><tr><th>Fecha</th><th>Tipo</th><th>Producto</th><th>Depósito</th><th>Lote</th><th>Cantidad</th></tr></thead><tbody>{movements.map(m=><tr key={m.id}><td>{new Date(m.occurred_at).toLocaleString("es-AR")}</td><td>{m.movement_type}</td><td>{m.product_name}</td><td>{m.warehouse_name}</td><td>{m.lot_number||m.serial_number||"—"}</td><td>{m.quantity} {m.consumption_unit}</td></tr>)}</tbody></table></div></div>}
    </div>
  );
}
